/**
 * 매장 API 서비스
 * @see SPEC-193-stores-list-api.md
 */
import { apiClient, validatePathParam } from './client';
import { recoverSafeRead } from './recoverSafeRead';
import { READ_REQUEST_ID_HEADER } from '@/utils/transportDiagnostics';
import type {
  StoreDetailResponse,
  MenuListResponse,
  StoreWithOrganization,
  StoreHoursResponse,
  StoreOpenCheckResponse,
} from './types';
import type { Currency } from '@/types';

/** 매장 목록 조회 파라미터 */
export interface GetStoresParams {
  /** 위도 (-90 ~ 90) - 위치 기반 검색 */
  lat?: number;
  /** 경도 (-180 ~ 180) - 위치 기반 검색 */
  lng?: number;
  /** 검색 반경 (km, 최대 100, 기본값 10) */
  radius?: number;
  /** 픽업 가능 매장만 조회 */
  pickup_available?: boolean;
  /** 조직 ID로 필터링 */
  organization_id?: number;
  /** 브랜드 ID로 필터링 */
  brand_id?: number;
  /** 지정 매장이 속한 활성 매장 그룹 기준으로 필터링 */
  source_store_id?: number;
  /** 정렬 기준 (distance, rating, reviews, name) */
  sort?: 'distance' | 'rating' | 'reviews' | 'name';
  /** 페이지당 항목 수 (최대 100, 기본값 20) */
  per_page?: number;
  /** 페이지 번호 */
  page?: number;
  /** Index signature for API client compatibility */
  [key: string]: string | number | boolean | undefined;
}

/** 매장 목록 응답 메타데이터 */
export interface StoreListMeta {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  /** 위치 기반 검색 시에만 포함 */
  search_center?: {
    lat: number;
    lng: number;
  };
  /** 위치 기반 검색 시에만 포함 */
  search_radius_km?: number;
}

/** 매장 목록 응답 (페이지네이션 포함) */
export interface StoreListResponse {
  data: StoreWithOrganization[];
  meta: StoreListMeta;
  links?: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
}

export interface GetMenusParams {
  category?: string;
  currency?: Currency;
  page?: number;
  per_page?: number;
  [key: string]: string | number | boolean | undefined;
}

export const storeApi = {
  /**
   * 매장 목록 조회
   * GET /api/v1/stores
   *
   * - 위치 정보 없이: 전체 매장 목록 (이름순)
   * - 위치 정보 있음: 근처 매장 목록 (거리순)
   */
  getStores: (params?: GetStoresParams, signal?: AbortSignal) =>
    apiClient.get<StoreListResponse>('/api/v1/stores', { params, signal }),

  /**
   * 매장 상세 정보 조회
   * GET /api/v1/stores/:storeParam
   * @param storeParam 매장 슬러그 또는 ID
   */
  getStore: (storeParam: string, signal?: AbortSignal) => {
    const safeParam = validatePathParam(storeParam, 'storeParam');
    return recoverSafeRead('store-read', signal, (timeout, requestId) =>
      apiClient.get<StoreDetailResponse>(`/api/v1/stores/${safeParam}`, {
        signal,
        timeout,
        ...(requestId ? { headers: { [READ_REQUEST_ID_HEADER]: requestId } } : {}),
      }),
    );
  },

  /**
   * 매장별 메뉴 목록 조회
   * GET /api/v1/stores/:storeParam/menus
   * @param storeParam 매장 슬러그 또는 ID
   */
  getMenus: (storeParam: string, params?: GetMenusParams, signal?: AbortSignal) => {
    const safeParam = validatePathParam(storeParam, 'storeParam');
    return apiClient.get<MenuListResponse>(`/api/v1/stores/${safeParam}/menus`, { params, signal });
  },

  /**
   * 매장 영업시간 상세 조회
   * GET /api/v1/stores/{storeId}/hours
   *
   * @see olulo-mx-admin/routes/api.php:555-556
   */
  getStoreHours: (storeId: number, params?: { start_date: string; end_date: string }) =>
    apiClient.get<StoreHoursResponse>(`/api/v1/stores/${storeId}/hours`, { params }),

  /**
   * 매장 영업 여부 실시간 확인
   * GET /api/v1/stores/{storeId}/hours/check
   *
   * @see olulo-mx-admin/routes/api.php:551-552
   */
  checkStoreOpen: (storeId: number) =>
    apiClient.get<StoreOpenCheckResponse>(`/api/v1/stores/${storeId}/hours/check`),
};
